/**
 * AutocolantesPage — Página dedicada (não modal) com passaporte digital,
 * coleção de autocolantes e carimbos. Layout rugged (fundo #0F0D0B + topo_bg
 * suave). Substitui o antigo AutocolantesPanel Dialog.
 */
import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Share2 } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const STICKERS = [
  { key: 'novato',      icon: '🚙', text: 'NOVATO 4×4',     sub: null,        color: '#F2EBDD', bg: '#3D362E', minXp: 0 },
  { key: 'trailblazer', icon: '🛞', text: 'TRAILBLAZER',    sub: 'PT · 4×4',  color: '#0F0D0B', bg: '#E87A2A', minXp: 500 },
  { key: 'summit',      icon: '🏔️', text: 'SUMMIT',        sub: null,        color: '#F2EBDD', bg: '#2B2A25', minXp: 1500 },
  { key: 'nightrider',  icon: '🌙', text: 'NIGHT RIDER',    sub: '☾ ★',       color: '#0F0D0B', bg: '#C4A275', minXp: 3000 },
  { key: 'expedition',  icon: '⛰️', text: 'EXPEDITION 4×4', sub: null,        color: '#F2EBDD', bg: '#4E5D3F', minXp: 5000 },
  { key: 'terrabruta',  icon: '🪨', text: 'TERRA BRUTA',    sub: 'PT · 4×4',  color: '#F2EBDD', bg: '#2B2A25', minXp: 8000 },
  { key: 'crest',       icon: '⚔️', text: 'BRASÃO',         sub: null,        color: '#FACC15', bg: '#1A1613', minXp: 12000 },
  { key: 'finisher',    icon: '🏁', text: 'FINISHER',       sub: '★★★',       color: '#0F0D0B', bg: '#FACC15', minXp: 20000 },
];

const STAMP_ICONS = ['⛰️','🌲','🏖️','🌊','🏔️','⚡','💦','🧭','🌙','☀️','🏁','🛞','🏆','🔥','👑','★','✧','✦'];

export default function AutocolantesPage() {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [vehicle, setVehicle] = useState({ decals: [] });
  const [stats, setStats] = useState({ xp_total: 0 });
  const [stampsCount, setStampsCount] = useState(0);

  useEffect(() => {
    if (authLoading) return;
    if (!user) { navigate('/login'); return; }
    setLoading(true);
    const opts = { withCredentials: true };
    Promise.all([
      axios.get(`${API}/gamification/vehicle/me`, opts).then((r) => r.data).catch(() => null),
      axios.get(`${API}/gamification/stats/me`,   opts).then((r) => r.data).catch(() => null),
      axios.get(`${API}/gamification/stamps/me`,  opts).then((r) => r.data).catch(() => null),
    ]).then(([v, s, stamps]) => {
      if (v) setVehicle(v);
      if (s) setStats(s);
      if (Array.isArray(stamps)) setStampsCount(stamps.length);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, [user, authLoading, navigate]);

  const xp = stats.xp_total || 0;
  const km = Math.round(stats.km_total || 0);
  const routes = stats.routes_completed || 0;
  const streak = stats.streak_days || 0;
  const owned = useMemo(() => new Set(vehicle?.decals || []), [vehicle]);
  const nextSticker = STICKERS.filter((s) => !owned.has(s.key) && s.minXp > xp)
    .sort((a, b) => a.minXp - b.minXp)[0];
  const tier = xp >= 20000 ? { name: 'PRATA', color: '#C0C0C0' }
             : xp >= 8000  ? { name: 'BRONZE II', color: '#B87333' }
             : xp >= 2000  ? { name: 'BRONZE I', color: '#8B5A2B' }
             : { name: 'INICIANTE', color: '#6B5D4B' };
  const initials = (user?.username || user?.name || user?.email || 'TC').slice(0, 2).toUpperCase();

  const claimSticker = async (s) => {
    if (owned.has(s.key)) return;
    if (s.minXp > xp) {
      toast.error('Autocolante bloqueado', {
        description: `Precisas de ${s.minXp.toLocaleString('pt-PT')} XP. Tens ${xp.toLocaleString('pt-PT')}.`,
      });
      return;
    }
    const next = [...(vehicle.decals || []), s.key];
    setVehicle({ ...vehicle, decals: next });
    try { await axios.put(`${API}/gamification/vehicle/me`, { decals: next }, { withCredentials: true }); toast.success(`${s.text} desbloqueado!`); } catch { /* offline ok */ }
  };

  const removeSticker = async (s) => {
    if (!owned.has(s.key)) return;
    const next = (vehicle.decals || []).filter((k) => k !== s.key);
    setVehicle({ ...vehicle, decals: next });
    try { await axios.put(`${API}/gamification/vehicle/me`, { decals: next }, { withCredentials: true }); } catch { /* offline ok */ }
  };

  const monoStyle = { fontFamily: "'JetBrains Mono', monospace" };

  return (
    <div
      className="min-h-screen"
      style={{
        background: '#0F0D0B',
        backgroundImage: 'url(/topo_bg.jpg)',
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundAttachment: 'fixed',
        backgroundBlendMode: 'overlay',
      }}
      data-testid="autocolantes-page"
    >
      {/* Overlay escuro */}
      <div style={{ position: 'fixed', inset: 0, background: 'linear-gradient(180deg, rgba(15,13,11,.75), rgba(15,13,11,.92))', pointerEvents: 'none' }} />

      {/* Top bar */}
      <div
        className="sticky top-0 z-10 flex items-center justify-between px-4 py-3 border-b"
        style={{ background: 'rgba(15,13,11,.95)', borderColor: '#4A443A', backdropFilter: 'blur(8px)' }}
        data-testid="autocolantes-topbar"
      >
        <button
          onClick={() => navigate('/dashboard')}
          className="flex items-center gap-2 text-[#F2EBDD] hover:text-[#E87A2A] transition"
          style={{ ...monoStyle, fontSize: 11, letterSpacing: 1.5, fontWeight: 900, textTransform: 'uppercase' }}
          data-testid="autocolantes-back-btn"
        >
          ← Voltar ao Dashboard
        </button>
        <div className="flex items-center gap-2">
          <span className="text-2xl">🏆</span>
          <div>
            <h1 style={{ ...monoStyle, fontSize: 16, letterSpacing: 3, fontWeight: 900, color: '#F2EBDD' }}>
              AUTOCOLANTES
            </h1>
            <p style={{ ...monoStyle, fontSize: 9, color: '#C4A275', letterSpacing: 1.5, marginTop: 2 }}>
              PASSAPORTE 4×4 · COLEÇÃO PESSOAL
            </p>
          </div>
        </div>
        <div style={{ width: 140 }} />
      </div>

      <div className="relative max-w-4xl mx-auto px-4 py-6 space-y-6">
        {loading ? (
          <div className="text-center py-20 text-[#C4A275]" style={monoStyle}>A carregar passaporte…</div>
        ) : (
          <>
            {/* PASSAPORTE */}
            <section
              className="rounded-xl border p-5"
              style={{ background: 'rgba(26,22,19,.85)', borderColor: '#4A443A', backdropFilter: 'blur(6px)' }}
              data-testid="autocolantes-passport"
            >
              <div className="flex items-center gap-4">
                <div
                  className="rounded-full border-4"
                  style={{
                    width: 68,
                    height: 68,
                    background: 'linear-gradient(135deg,#C4A275,#E87A2A)',
                    borderColor: '#E87A2A',
                    display: 'grid',
                    placeItems: 'center',
                    ...monoStyle,
                    fontSize: 20,
                    fontWeight: 900,
                    color: '#0F0D0B',
                  }}
                >
                  {initials}
                </div>
                <div className="flex-1 min-w-0">
                  <div style={{ ...monoStyle, fontSize: 18, fontWeight: 900, color: '#F2EBDD', letterSpacing: 1 }}>
                    {user?.username || user?.name || 'Piloto Tectur'}
                  </div>
                  <div style={{ ...monoStyle, fontSize: 10, color: '#C4A275', letterSpacing: 1.5, marginTop: 3 }}>
                    ◈ 4×4 CLUB · MEMBRO {user?.role === 'admin' ? '· ADMIN' : user?.role === 'staff' ? '· STAFF' : ''}
                  </div>
                </div>
                <div
                  className="px-3 py-1.5 rounded"
                  style={{ background: tier.color, color: '#0F0D0B', ...monoStyle, fontSize: 11, fontWeight: 900, letterSpacing: 2 }}
                  data-testid="autocolantes-tier"
                >
                  {tier.name}
                </div>
              </div>

              <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mt-4">
                <StatCell value={xp.toLocaleString('pt-PT')} label="XP TOTAL" testid="stat-xp" />
                <StatCell value={km.toLocaleString('pt-PT')} label="KM" testid="stat-km" />
                <StatCell value={routes} label="ROTAS" testid="stat-routes" />
                <StatCell value={`${streak}d`} label="STREAK" testid="stat-streak" />
              </div>

              {nextSticker && (
                <div className="mt-4">
                  <div className="flex justify-between mb-2" style={{ ...monoStyle, fontSize: 10, color: '#C4A275' }}>
                    <span>PRÓXIMO · <b style={{ color: '#F2EBDD' }}>{nextSticker.text}</b></span>
                    <span>{(nextSticker.minXp - xp).toLocaleString('pt-PT')} XP em falta</span>
                  </div>
                  <div className="h-2.5 rounded-full overflow-hidden" style={{ background: '#0F0D0B', border: '1px solid rgba(74,68,58,.6)' }}>
                    <div
                      className="h-full"
                      style={{
                        width: `${Math.min(100, (xp / (nextSticker?.minXp || 1)) * 100)}%`,
                        background: 'linear-gradient(90deg, #E87A2A, #FACC15)',
                        transition: 'width .6s ease',
                      }}
                    />
                  </div>
                </div>
              )}
            </section>

            {/* AUTOCOLANTES */}
            <section data-testid="autocolantes-collection-section">
              <SectionHeader title="▸ COLEÇÃO DE AUTOCOLANTES" count={`${owned.size} / ${STICKERS.length}`} />
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-3">
                {STICKERS.map((s) => {
                  const isOwned = owned.has(s.key);
                  const locked = !isOwned && s.minXp > xp;
                  return (
                    <button
                      key={s.key}
                      onClick={() => (isOwned ? removeSticker(s) : claimSticker(s))}
                      className="aspect-square rounded-lg p-3 flex flex-col items-center justify-center relative overflow-hidden border-2 transition"
                      style={{
                        borderStyle: locked ? 'dashed' : 'solid',
                        borderColor: locked ? 'rgba(196,162,117,.25)' : (isOwned ? 'rgba(0,0,0,.4)' : 'rgba(196,162,117,.20)'),
                        background: isOwned ? s.bg : (locked ? 'rgba(26,22,19,.40)' : 'rgba(26,22,19,.85)'),
                        opacity: locked ? 0.55 : 1,
                        boxShadow: isOwned ? '0 6px 18px rgba(0,0,0,.35)' : 'none',
                      }}
                      data-testid={`web-sticker-${s.key}`}
                      title={isOwned ? 'Clica para remover' : (locked ? `Precisa ${s.minXp.toLocaleString('pt-PT')} XP` : 'Clica para desbloquear')}
                    >
                      {locked && <span style={{ position: 'absolute', top: 6, right: 8, fontSize: 12, opacity: 0.75 }}>🔒</span>}
                      {isOwned && <span style={{ position: 'absolute', top: 6, right: 8, fontSize: 12, color: s.color }}>✓</span>}
                      <span style={{ fontSize: 36, lineHeight: 1, marginBottom: 6 }}>{s.icon}</span>
                      <div style={{ ...monoStyle, fontWeight: 900, fontSize: 10, color: isOwned ? s.color : '#F2EBDD', letterSpacing: 1, textAlign: 'center' }}>
                        {s.text}
                      </div>
                      {s.sub && (
                        <div style={{ ...monoStyle, fontWeight: 700, fontSize: 8, color: isOwned ? s.color : '#F2EBDD', letterSpacing: 1, opacity: 0.85, marginTop: 2 }}>
                          {s.sub}
                        </div>
                      )}
                      {locked && (
                        <div style={{ ...monoStyle, fontWeight: 900, fontSize: 9, color: '#C4A275', letterSpacing: 1, marginTop: 4 }}>
                          {s.minXp.toLocaleString('pt-PT')} XP
                        </div>
                      )}
                    </button>
                  );
                })}
              </div>
            </section>

            {/* CARIMBOS */}
            <section data-testid="autocolantes-stamps-section">
              <SectionHeader title="▸ CARIMBOS DO PASSAPORTE" count={`${stampsCount} / ${STAMP_ICONS.length}`} />
              <div className="grid grid-cols-6 md:grid-cols-9 gap-3 mt-3">
                {STAMP_ICONS.map((icon, i) => {
                  const unlocked = i < stampsCount;
                  return (
                    <div
                      key={i}
                      className="aspect-square rounded-full border-2 flex items-center justify-center relative"
                      style={{
                        borderStyle: unlocked ? 'solid' : 'dashed',
                        borderColor: unlocked ? '#E87A2A' : 'rgba(196,162,117,.25)',
                        background: unlocked ? 'rgba(232,122,42,.10)' : 'rgba(26,22,19,.85)',
                        opacity: unlocked ? 1 : 0.4,
                        filter: unlocked ? 'none' : 'grayscale(1)',
                      }}
                    >
                      <span style={{ fontSize: 22 }}>{icon}</span>
                      <div
                        className="absolute -bottom-1 -right-1 rounded-full border-2 flex items-center justify-center"
                        style={{
                          width: 18,
                          height: 18,
                          background: unlocked ? '#E87A2A' : '#241E19',
                          borderColor: '#0F0D0B',
                          color: unlocked ? '#0F0D0B' : '#C4A275',
                          ...monoStyle,
                          fontSize: 10,
                          fontWeight: 900,
                        }}
                      >
                        {unlocked ? '✓' : '🔒'}
                      </div>
                    </div>
                  );
                })}
              </div>
            </section>

            {/* CTA */}
            {nextSticker && (
              <section
                className="rounded-lg border p-4 text-center"
                style={{ borderColor: 'rgba(232,122,42,.35)', background: 'rgba(232,122,42,.10)' }}
                data-testid="autocolantes-cta"
              >
                <div style={{ ...monoStyle, fontWeight: 900, fontSize: 12, color: '#E87A2A', letterSpacing: 2.5, marginBottom: 6 }}>
                  ▸ PRÓXIMO DESBLOQUEIO
                </div>
                <div style={{ fontSize: 14, color: '#F2EBDD' }}>
                  Faltam <b style={{ color: '#E87A2A', ...monoStyle }}>{(nextSticker.minXp - xp).toLocaleString('pt-PT')} XP</b> para desbloqueares <b style={{ color: '#E87A2A' }}>{nextSticker.text}</b>.
                </div>
                <div style={{ fontSize: 12, color: '#C4A275', marginTop: 6, fontStyle: 'italic' }}>
                  Dica: rotas Difícil / Expert dão mais XP por km.
                </div>
              </section>
            )}

            <div className="text-center pt-2 pb-6 flex flex-wrap gap-3 justify-center">
              <Button
                onClick={() => {
                  const url = `${window.location.origin}/passport/${user?.user_id}`;
                  navigator.clipboard.writeText(url).then(() => {
                    toast.success('Link do passaporte copiado!', {
                      description: 'Partilha com os teus amigos 4×4',
                      action: {
                        label: 'Abrir',
                        onClick: () => window.open(url, '_blank'),
                      },
                    });
                  }).catch(() => {
                    toast.error('Não foi possível copiar', { description: url });
                  });
                }}
                style={{ background: 'rgba(232,122,42,.15)', color: '#E87A2A', border: '1px solid #E87A2A', ...monoStyle, fontWeight: 900, letterSpacing: 2, textTransform: 'uppercase', fontSize: 11 }}
                data-testid="share-passport-btn"
              >
                <Share2 className="w-4 h-4 mr-2" /> Partilhar Passaporte
              </Button>
              <Button
                onClick={() => navigate('/dashboard')}
                style={{ background: '#E87A2A', color: '#0F0D0B', ...monoStyle, fontWeight: 900, letterSpacing: 2, textTransform: 'uppercase', fontSize: 11 }}
                data-testid="autocolantes-back-btn-bottom"
              >
                ← Voltar ao Dashboard
              </Button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function StatCell({ value, label, testid }) {
  const monoStyle = { fontFamily: "'JetBrains Mono', monospace" };
  return (
    <div
      className="rounded-lg p-3 text-center border"
      style={{ background: 'rgba(15,13,11,.60)', borderColor: 'rgba(196,162,117,.15)' }}
      data-testid={testid}
    >
      <div style={{ ...monoStyle, fontWeight: 900, fontSize: 20, color: '#E87A2A', lineHeight: 1 }}>{value}</div>
      <div style={{ ...monoStyle, fontWeight: 900, fontSize: 9, color: '#C4A275', letterSpacing: 1.2, marginTop: 4 }}>{label}</div>
    </div>
  );
}

function SectionHeader({ title, count }) {
  const monoStyle = { fontFamily: "'JetBrains Mono', monospace" };
  return (
    <div className="flex items-baseline justify-between">
      <h3 style={{ ...monoStyle, fontWeight: 900, fontSize: 13, color: '#E87A2A', letterSpacing: 3 }}>{title}</h3>
      <span style={{ ...monoStyle, fontSize: 11, color: 'rgba(242,235,221,.55)' }}>{count}</span>
    </div>
  );
}
